// ---- Cinematic — GAME integration (plan 26) ----
// The game-side half of the cinematic feature: owns the STAGE world (built from
// the cinematic's map doc — remember the one-active-map rule: the stage and the
// live world are NEVER hot at once; game.ts sequences every build/teardown on
// its swapChain) and the module-held playback, and exposes the per-frame
// updateCinematic() the frame loop's cinematic branch drives. The world-swap
// choreography itself (dispose live world → stage → play → rebuild) lives in
// game.ts, which has the initWorld/disposeWorld privates — talk.ts requests a
// cinematic through requestCinematic(), and game.ts registers the launcher.
//
// The studio never imports this module — it mounts runtime.ts over worldstage.

import * as THREE from "three";
import { game } from "../state.ts";
import type { CinematicDoc, MapDoc } from "../content/schemas.ts";
import { activateMap } from "../content/load.ts";
import { WATER_LEVEL } from "../map/active.ts";
import { buildWorld, freezeStatic } from "../world.ts";
import { surfaceAt, type Surface } from "../physics.ts";
import { rippleWater } from "../terrain.ts";
import { disposeGroup } from "../meshkit.ts";
import { registerCulled, updateDecoView, type CulledDeco } from "../instanced.ts";
import { weatherClear } from "../weather.ts";
import { setAudioMap, setAudioAnchor } from "../audio.ts";
import { fadeOut, fadeIn } from "../ui.ts";
import { playCinematic, type CinePlayback } from "./runtime.ts";
import type { Appearance, Gear } from "../shared.ts";

let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;

// The viewer's hero (the "player" actor's look/gear/name) — stashed from the
// spawn message by game.ts (setCineHero) since there's no other appearance store.
let heroLook: number | Partial<Appearance> = {};
let heroGear: Gear = {};
let heroName = "You";

// The live stage + playback (exactly one cinematic at a time).
let stageWorld: THREE.Group | null = null;
let stageSurfaces: Surface[] = [];
let stageWater: THREE.Object3D | null = null;
let playback: CinePlayback | null = null;

// game.ts registers the launcher that runs the talk-triggered there-and-back
// flow (it needs game.ts's world lifecycle privates); talk.ts calls request.
let launcher: ((id: string) => void) | null = null;
export function setCinematicLauncher(fn: (id: string) => void): void {
  launcher = fn;
}
export function requestCinematic(id: string): void {
  if (!launcher) {
    console.warn(`playCinematic "${id}" requested before the launcher registered`);
    return;
  }
  launcher(id);
}

/** Boot wiring. Registered on the CAPTURE phase so the cutscene swallows the
 *  keyboard ahead of every bubble-phase listener (chat/input/interact/focus),
 *  regardless of boot registration order. Esc = skip (doc-gated). */
export function initCinematic(s: THREE.Scene, cam: THREE.PerspectiveCamera): void {
  scene = s;
  camera = cam;
  window.addEventListener(
    "keydown",
    (e) => {
      if (!game.cinematic) return;
      e.stopImmediatePropagation(); // a cutscene owns the keyboard outright
      if (e.key === "Escape") playback?.skip();
    },
    true,
  );
}

export function setCineHero(look: number | Partial<Appearance>, gear: Gear, name: string): void {
  heroLook = look;
  heroGear = gear;
  heroName = name;
}

/** Build the cinematic's stage world. Caller guarantees NO live world is built
 *  (login: before the first initWorld; talk flow: after disposeWorld) — this
 *  repoints the active-map globals at the stage doc. */
export function buildCineStage(mapDoc: MapDoc, doc: CinematicDoc): void {
  if (!scene) throw new Error("initCinematic() must run before buildCineStage()");
  activateMap(mapDoc);
  // The stage map's ambience/audioZones play during the cutscene, anchored to
  // the CAMERA (the shot travels; game.player idles at its pre-cinematic spot —
  // the same reasoning as updateCinematic's camera-anchored updateDecoView).
  setAudioMap(mapDoc);
  setAudioAnchor(camera);
  stageWorld = buildWorld(mapDoc);
  scene.add(stageWorld);
  // Same static-matrix bake as initWorld (game.ts) — the stage is a full map
  // build and pays the identical per-frame recompose cost without it. Puppets
  // attach to the SCENE (playCinematic's parent), never under the stage, so
  // the freeze can't touch anything the runtime animates.
  freezeStatic(stageWorld);
  stageSurfaces = (stageWorld.userData.surfaces ?? []) as Surface[];
  stageWater = stageWorld.getObjectByName("water") ?? null;
  registerCulled((stageWorld.userData.culledDecos ?? []) as CulledDeco[]);
  if (doc.timeOfDay !== undefined) game.tod = doc.timeOfDay;
  // Same rule as a portal arrival: a weather-off stage map must not inherit the
  // live map's precipitation (the tutorial stage is weather:false).
  if (mapDoc.env?.weather === false) weatherClear();
}

/** Mount the shared runtime over the stage and flip the world into cinematic
 *  mode. Resolve of `.done` = the film ended (finished or skipped); the caller
 *  fades, THEN calls disposeCineStage(). */
export function mountCineRuntime(doc: CinematicDoc): CinePlayback {
  if (!scene || !camera) throw new Error("initCinematic() must run before mountCineRuntime()");
  playback = playCinematic(doc, {
    camera,
    parent: scene,
    surfaceY: (x, z, prev) => surfaceAt(x, z, prev, stageSurfaces),
    waterY: () => WATER_LEVEL, // active-map waterline (buildCineStage activated the stage doc)
    playerLookGear: () => ({ look: heroLook, gear: heroGear }),
    playerName: () => heroName,
    fade: (dir, ms) => { void (dir === "out" ? fadeOut(ms) : fadeIn(ms)); },
  });
  game.cinematic = true;
  return playback;
}

/** Tear the stage down (mirror of buildCineStage + mountCineRuntime). Runs
 *  behind the caller's fade; the caller rebuilds the live world right after. */
export function disposeCineStage(): void {
  playback?.dispose();
  playback = null;
  setAudioAnchor(null); // back to the player; the caller's initWorld re-points the map
  registerCulled([]);
  if (stageWorld && scene) {
    scene.remove(stageWorld);
    disposeGroup(stageWorld);
  }
  stageWorld = null;
  stageSurfaces = [];
  stageWater = null;
  game.cinematic = false;
}

/** The cinematic frame step (game.ts's early frame branch): puppets + camera +
 *  letterbox via the runtime, plus the stage ambiance the normal frame path
 *  would have run (water ripple ~20Hz, deco culling around the CAMERA — the
 *  shot moves, the player doesn't). dayNight/weather/lamps stay in game.ts
 *  (their ordering contract lives there). */
export function updateCinematic(dt: number, t: number): void {
  if (!playback || !camera) return;
  playback.tick(dt, t);
  if (stageWater) rippleWater(t); // one uniform write — the wave runs on the GPU
  updateDecoView(camera.position.x, camera.position.z);
}
